import { useContext, createContext, PropsWithChildren } from 'react';
import { useMappings } from '../hooks/queries';
import { ClientDataType } from '../types';
import { MappingMetadata } from '../hooks/queries/mappings/types';

export interface FieldMapping {
  current: ClientDataType;
}

export type FieldMappingContextState = {
  customEventMappings: MappingMetadata[];
  isCustomEventMappingsLoading: boolean;
  didCustomEventMappingsError: boolean;
  userEventMappings: MappingMetadata[];
  isUserEventMappingsLoading: boolean;
  didUserEventMappingsError: boolean;
  purchaseEventMappings: MappingMetadata[];
  isPurchaseEventMappingsLoading: boolean;
  didPurchaseEventMappingsError: boolean;
};

export const defaultState: FieldMappingContextState = {
  customEventMappings: [],
  isCustomEventMappingsLoading: false,
  didCustomEventMappingsError: false,
  userEventMappings: [],
  isUserEventMappingsLoading: false,
  didUserEventMappingsError: false,
  purchaseEventMappings: [],
  isPurchaseEventMappingsLoading: false,
  didPurchaseEventMappingsError: false,
};

export const FieldMappingsContext = createContext<FieldMappingContextState>(defaultState);

/**
 * This context stores all expensive field mappings (user, custom event, purchase) in queryBuilder.
 * If the esIndex is Catalog, any of these will return the catalog field mappings and would only be called once
 **/
export const useFieldMappingsContext = () => useContext(FieldMappingsContext);

export function FieldMappingsContextProvider({
  children,
}: PropsWithChildren<Record<string, unknown>>) {
  const {
    mappings: customEventMappings,
    isLoading: isCustomEventMappingsLoading,
    didError: didCustomEventMappingsError,
  } = useMappings({ dataType: ClientDataType.CustomEvent });

  const {
    mappings: userEventMappings,
    isLoading: isUserEventMappingsLoading,
    didError: didUserEventMappingsError,
  } = useMappings({ dataType: ClientDataType.User });

  const {
    mappings: purchaseEventMappings,
    isLoading: isPurchaseEventMappingsLoading,
    didError: didPurchaseEventMappingsError,
  } = useMappings({ dataType: ClientDataType.Purchase });

  const value = {
    customEventMappings,
    isCustomEventMappingsLoading,
    didCustomEventMappingsError,
    userEventMappings,
    isUserEventMappingsLoading,
    didUserEventMappingsError,
    purchaseEventMappings,
    isPurchaseEventMappingsLoading,
    didPurchaseEventMappingsError,
  };

  return <FieldMappingsContext.Provider value={value}>{children}</FieldMappingsContext.Provider>;
}
